import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { BreakdownIncident } from '../lib/types'
import { fetchBreakdownHistory } from '../lib/breakdownApi'

function when(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

/** Past incidents for this vehicle -- resolved ones only, the active one already has its
 *  own status block above. Each shows what happened right after: resumed_moving_ts (a
 *  human's guess it was fixed) or cleared_ts (a human's confirmation), then the next two
 *  geotags actually recorded, so a repeat "fixed" that quietly broke down again 4 km later
 *  is visible instead of buried. */
export function BreakdownHistory({ vehicleId }: { vehicleId: string }): JSX.Element | null {
  const [incidents, setIncidents] = useState<BreakdownIncident[]>([])
  const [loaded, setLoaded] = useState(false)
  const [open, setOpen] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchBreakdownHistory(vehicleId)
      .then((rows) => { if (!cancelled) setIncidents(rows) })
      .catch(() => { /* history is a nice-to-have below the fold; a form must not block on it */ })
      .finally(() => { if (!cancelled) setLoaded(true) })
    return () => { cancelled = true }
  }, [vehicleId])

  const past = incidents.filter((i) => !i.active)
  if (!loaded || past.length === 0) return null

  return (
    <div className="grid gap-1 border-t border-[rgba(15,23,42,0.07)] pt-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-[#8794A7]">
        Past incidents ({past.length})
      </div>
      {past.map((i) => (
        <div key={i.breakdown_id} className="rounded-lg border border-[rgba(15,23,42,0.08)]">
          <button type="button"
                  onClick={() => setOpen(open === i.breakdown_id ? null : i.breakdown_id)}
                  className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-[11.5px] text-[#0F172A]">
            {open === i.breakdown_id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <span className="font-medium">{i.category ?? 'Breakdown'}</span>
            <span className="text-[#8794A7]">· {when(i.opened_ts)} → {when(i.cleared_ts)}</span>
          </button>
          {open === i.breakdown_id && (
            <div className="grid gap-1.5 border-t border-[rgba(15,23,42,0.06)] px-2.5 py-2 text-[11px] text-[#475569]">
              {i.note && <p>{i.note}</p>}
              {i.resumed_moving_ts && (
                <p className="text-[#8794A7]">started moving at {when(i.resumed_moving_ts)}</p>
              )}
              {i.next_geotags.length > 0 ? (
                <div>
                  <div className="text-[10px] font-medium uppercase tracking-wide text-[#8794A7]">
                    Next after resolution
                  </div>
                  <ul className="mt-0.5 grid gap-0.5">
                    {i.next_geotags.map((g, idx) => (
                      <li key={idx} className="font-mono text-[10.5px] text-[#475569]">
                        {when(g.ts)} · {g.event} {g.geofence_name ? `@ ${g.geofence_name}` : ''}
                        {g.site ? ` (${g.site})` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-[#8794A7]">No geotags recorded after this incident closed.</p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
